import { Button, DatePicker, Input, InputNumber, Select, Table } from 'antd';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaPlanList } from './hooks/useMediaPlanList';
import { NoData, MediaPlanForm, Loader } from '@/components';
import {
  FilterField,
  FilterFieldWide,
  FilterGrid,
  FilterHeader,
  FilterHeaderActions,
  FilterHeaderSearch,
  FilterHeaderTopRow,
  FilterLabel,
  StyledContainer
} from './MediaPlanList.styles';
import { useRole } from '@/hooks/useRole';
import { useResizableColumns } from '@/hooks/useResizableColumns';
import ResizableHeaderCell from '@/components/ResizableHeaderCell';
import { APP_SCOPE, NAMESPACE } from '@/constants';
import dayjs from 'dayjs';

const { Search } = Input;
const { RangePicker } = DatePicker;

const renderTotalItemsCount = (total: number) => `Total ${total} items`;

const MediaPlanList: FC = () => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { canCreate } = useRole(APP_SCOPE.MEDIA_PLAN);
  const {
    columns: baseColumns,
    count,
    data,
    filters,
    resetAllFilters,
    amOptions,
    clientOptions,
    dealIdOptions,
    statusOptions,
    filterValues,
    setFilterValues,
    handleFormClose,
    handleFormOpen,
    handleOnRow,
    handlePaginationChange,
    handleSearch,
    handleTableChange,
    isFetching,
    isFormOpen,
    isLoading,
    limit,
    mediaPlan,
    page,
    search
  } = useMediaPlanList();
  const { columns, scrollX } = useResizableColumns('nrve.mediaPlan.listColumnWidths', baseColumns);

  const hasActiveFilterValues = !!filterValues && Object.values(filterValues).some((v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
  const hasTableFilters =
    !!filters && Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : v != null && v !== ''));
  const canResetFilters = hasActiveFilterValues || !!search?.trim() || hasTableFilters;

  const renderCreateMediaPlanButton = useCallback(
    () =>
      canCreate ? (
        <Button onClick={handleFormOpen} type="primary" size="large">
          {t('createMediaPlan')}
        </Button>
      ) : (
        <></>
      ),
    [handleFormOpen, canCreate, t]
  );

  const MediaPlanFormComponent = (
    <MediaPlanForm isOpen={isFormOpen} close={handleFormClose} mediaPlan={mediaPlan} />
  );

  if (isLoading) {
    return <Loader fullscreen={false} />;
  }

  // Only show the "no data / create your first plan" state when there are truly no plans
  // AND the user isn't currently applying any filters/search.
  if (count === 0 && !filters && !search && !hasActiveFilterValues) {
    return (
      <>
        {MediaPlanFormComponent}
        <NoData
          title={t('noData', { ns: NAMESPACE.COMMON })}
          {...(canCreate && { subtitle: t('pleaseAddYourFirstMediaPlan') })}
        >
          {renderCreateMediaPlanButton()}
        </NoData>
      </>
    );
  }

  return (
    <StyledContainer>
      {MediaPlanFormComponent}
      <FilterHeader>
        <FilterHeaderTopRow>
          <FilterHeaderSearch>
            <Search
              size="large"
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by campaign, client, deal ID, plan ID…"
              defaultValue={search}
              allowClear
            />
          </FilterHeaderSearch>
          <FilterHeaderActions>
            <Button
              size="large"
              className="media-plan-reset-filters-btn"
              onClick={() => resetAllFilters?.()}
              disabled={!canResetFilters}
            >
              Reset filters
            </Button>
            {renderCreateMediaPlanButton()}
          </FilterHeaderActions>
        </FilterHeaderTopRow>

        <FilterGrid>
          <FilterField>
            <FilterLabel>Deal ID</FilterLabel>
            <Select
              size="large"
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Any"
              options={dealIdOptions}
              value={filterValues?.dealIds}
              optionFilterProp="label"
              onChange={(v) => setFilterValues?.({ dealIds: v })}
              style={{ width: '100%' }}
            />
          </FilterField>

          <FilterField>
            <FilterLabel>Account manager</FilterLabel>
            <Select
              size="large"
              allowClear
              showSearch
              placeholder="Any"
              options={amOptions}
              value={filterValues?.managerId}
              optionFilterProp="label"
              onChange={(v) => setFilterValues?.({ managerId: v })}
              style={{ width: '100%' }}
            />
          </FilterField>

          <FilterField>
            <FilterLabel>Client</FilterLabel>
            <Select
              size="large"
              allowClear
              showSearch
              placeholder="Any"
              options={clientOptions}
              value={filterValues?.clientId}
              optionFilterProp="label"
              onChange={(v) => setFilterValues?.({ clientId: v })}
              style={{ width: '100%' }}
            />
          </FilterField>

          <FilterField>
            <FilterLabel>Status</FilterLabel>
            <Select
              size="large"
              allowClear
              placeholder="Any"
              options={statusOptions}
              value={filterValues?.status}
              onChange={(v) => setFilterValues?.({ status: v })}
              style={{ width: '100%' }}
            />
          </FilterField>

          <FilterFieldWide>
            <FilterLabel>Start date range</FilterLabel>
            <RangePicker
              size="large"
              allowClear
              placeholder={['From', 'To']}
              style={{ width: '100%' }}
              value={
                filterValues?.startDateFrom && filterValues?.startDateTo
                  ? [dayjs(filterValues.startDateFrom), dayjs(filterValues.startDateTo)]
                  : null
              }
              onChange={(range) =>
                setFilterValues?.({
                  startDateFrom: range?.[0]?.toISOString(),
                  startDateTo: range?.[1]?.toISOString()
                })
              }
            />
          </FilterFieldWide>

          <FilterField>
            <FilterLabel>Min budget</FilterLabel>
            <InputNumber
              size="large"
              min={0}
              placeholder="0"
              style={{ width: '100%' }}
              value={filterValues?.budgetMin ?? null}
              onChange={(v) => setFilterValues?.({ budgetMin: typeof v === 'number' ? v : undefined })}
            />
          </FilterField>

          <FilterField>
            <FilterLabel>Max budget</FilterLabel>
            <InputNumber
              size="large"
              min={0}
              placeholder="No max"
              style={{ width: '100%' }}
              value={filterValues?.budgetMax ?? null}
              onChange={(v) => setFilterValues?.({ budgetMax: typeof v === 'number' ? v : undefined })}
            />
          </FilterField>
        </FilterGrid>
      </FilterHeader>
      <Table
        onRow={handleOnRow}
        onChange={handleTableChange}
        scroll={{ y: 570, x: scrollX }}
        components={{ header: { cell: ResizableHeaderCell } }}
        columns={columns}
        loading={isFetching}
        pagination={{
          disabled: isFetching,
          showQuickJumper: true,
          totalBoundaryShowSizeChanger: 10,
          showTotal: renderTotalItemsCount,
          defaultPageSize: limit,
          total: count,
          onChange: handlePaginationChange,
          current: page
        }}
        dataSource={data}
      />
    </StyledContainer>
  );
};

export default MediaPlanList;
